import React, { Fragment, useCallback, useEffect, useState } from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { Pressable, Text, useWindowDimensions, View } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import { useDebounce } from "use-debounce";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  convexAction,
  convexQuery,
  useConvexAction,
} from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type LocationData = {
  address: string;
  latitude?: number;
  longitude?: number;
};

export type LocationSheetPayload = {
  childId: Id<"students">;
  tutorId: Id<"tutorProfiles">;
};

export type LocationSheetResult = {
  location: LocationData;
};

export function toBookingLocation(location: LocationData): LocationData {
  const address = location.address.trim();
  if (location.latitude !== undefined && location.longitude !== undefined) {
    return {
      address,
      latitude: location.latitude,
      longitude: location.longitude,
    };
  }
  return { address };
}

function LocationSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    LocationSheetPayload,
    LocationSheetResult
  >(SheetIds.LOCATION);
  const responsiveWidth = useResponsiveWidth();
  const { childId, tutorId } = sheet?.payload ?? {};
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;
  const [address, setAddress] = useState("");
  const [value] = useDebounce(address, 1000);
  const trimmedAddress = address.trim();

  const { data: autoCompleteByAddress } = useQuery({
    ...convexAction(api.parent.bookLesson.actions.getAutoCompleteByAddress, {
      address: value,
    }),
    enabled: !!value,
  });
  const { mutateAsync: getPlaceDetailsByPlaceId } = useMutation({
    mutationFn: useConvexAction(
      api.parent.bookLesson.actions.getPlaceDetailsByPlaceId
    ),
  });
  const predictions = autoCompleteByAddress?.predictions ?? [];
  const { data: locations = [] } = useQuery(
    convexQuery(
      api.parent.bookLesson.queries.getPreviousLocations,
      tutorId && childId
        ? { studentId: childId, tutorId }
        : "skip",
    ),
  );
  const previousLocations = locations.filter((loc) => !!loc.address?.trim());

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        setAddress("");
        dismiss();
      }
    },
    [dismiss]
  );

  const onClose = useCallback(() => {
    setAddress("");
    close();
  }, [close]);

  const onUseTypedAddress = useCallback(() => {
    if (!trimmedAddress) return;
    close({
      location: { address: trimmedAddress },
    });
    setAddress("");
  }, [close, trimmedAddress]);

  const onSaveAddress = useCallback(
    async (placeId: string) => {
      const placeDetails = await getPlaceDetailsByPlaceId({ placeId });
      const formattedAddress = placeDetails?.result?.formatted_address;
      const geometry = placeDetails?.result?.geometry;
      if (!formattedAddress) return;

      const latitude = geometry?.location?.lat;
      const longitude = geometry?.location?.lng;
      close({
        location: toBookingLocation({
          address: formattedAddress,
          latitude,
          longitude,
        }),
      });
      setAddress("");
    },
    [close, getPlaceDetailsByPlaceId]
  );

  const onSelectAddress = useCallback(
    (location: LocationData) => {
      close({ location: toBookingLocation(location) });
      setAddress("");
    },
    [close]
  );

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      containerStyle={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetView>
        <View className="bg-teachLoop-creamBase rounded-t-[20]">
          <View className="h-6" />
          <View className="mx-5 flex-row justify-between">
            <Text className="text-xl font-geologica-medium font-medium text-black">
              Enter lesson location
            </Text>
            <Pressable onPress={onClose} className="active:opacity-80">
              <CloseIcon
                className="size-6"
                pathClassName="stroke-teachLoop-textPrimary"
              />
            </Pressable>
          </View>
          <View className="h-6" />

          <View className="mx-5">
            <Input
              placeholder="Search or type your address"
              value={address}
              onChangeText={setAddress}
              returnKeyType="done"
              onSubmitEditing={onUseTypedAddress}
            />
            <View className="h-2" />
            <Text className="text-sm font-geologica-regular font-normal leading-[1.5] text-gray-500">
              If it isn't listed, type the community, villa or apartment, and a
              nearby landmark.
            </Text>
            {trimmedAddress.length > 0 ? (
              <Fragment>
                <View className="h-6" />
                <Pressable onPress={onUseTypedAddress}>
                  <Text className="mx-3 text-sm font-geologica-medium font-medium leading-[1.5] text-gray-600">
                    Use this address
                  </Text>
                  <Text
                    className="m-3 mt-1 text-base font-geologica-regular font-normal leading-[1.5] text-black"
                    numberOfLines={2}
                  >
                    {trimmedAddress}
                  </Text>
                </Pressable>
                {predictions.length > 0 && (
                  <Fragment>
                    <View className="h-[1px] bg-black opacity-10" />
                    {predictions.map((item: any, index: number) => (
                      <Fragment key={item.place_id}>
                        <Pressable onPress={() => onSaveAddress(item.place_id)}>
                          <Text
                            className="m-3 text-base font-geologica-regular font-normal leading-[1.5] text-black"
                            numberOfLines={1}
                          >
                            {item.description}
                          </Text>
                        </Pressable>
                        {index !== predictions.length - 1 && (
                          <View className="h-[1px] bg-black opacity-10" />
                        )}
                      </Fragment>
                    ))}
                  </Fragment>
                )}
              </Fragment>
            ) : previousLocations.length > 0 ? (
              <Fragment>
                <View className="h-6" />
                <Text
                  className="m-3 text-base font-geologica-regular font-normal leading-[1.5] text-black"
                  numberOfLines={1}
                >
                  Or Choose from below
                </Text>
                <View className="h-6" />

                {previousLocations.map((item, index: number) => (
                  <Fragment key={`${item.address}-${index}`}>
                    <Pressable onPress={() => onSelectAddress(item)}>
                      <Text
                        className="m-3 text-base font-geologica-regular font-normal leading-[1.5] text-black"
                        numberOfLines={1}
                      >
                        {item.address}
                      </Text>
                    </Pressable>
                    {index !== previousLocations.length - 1 && (
                      <View className="h-[1px] bg-black opacity-10" />
                    )}
                  </Fragment>
                ))}
              </Fragment>
            ) : (
              <Fragment />
            )}
          </View>
          {trimmedAddress.length > 0 && (
            <View className="m-5 mt-8">
              <Button
                className="w-full"
                size="md"
                intent="primary"
                onPress={onUseTypedAddress}
              >
                <Button.Text size="md">Use this address</Button.Text>
              </Button>
            </View>
          )}
          {trimmedAddress.length === 0 && <View className="h-8" />}
        </View>
      </BottomSheetView>
    </BottomSheet>
  ) : null;
}

export default LocationSheet;
